// D,E,N 빼고 합산
export const calculateOtherTotal = (
  counts: Record<string, number>,
  excludeCodes: string[] = ["D", "E", "N"]
) => {
  return Object.entries(counts).reduce((total, [code, count]) => {
    if (!excludeCodes.includes(code) && code !== "") {
      return total + count;
    }
    return total;
  }, 0);
};

/** 근무표 행: RN → AN → ETC → 그 외 (비교는 role 문자열을 대문자로 통일) */
const NURSE_ROLE_ORDER = ["RN", "AN", "ETC"] as const;

export function nurseRoleSortIndex(role: string | null | undefined): number {
  const u = String(role ?? "")
    .trim()
    .toUpperCase();
  // 직군 미지정은 항상 맨 마지막으로 보낸다.
  const isUnassigned =
    u === "" ||
    u === "미지정".toUpperCase() ||
    u === "UNASSIGNED" ||
    u === "NONE" ||
    u === "NULL";
  if (isUnassigned) return NURSE_ROLE_ORDER.length + 1;
  const idx = (NURSE_ROLE_ORDER as readonly string[]).indexOf(u);
  return idx === -1 ? NURSE_ROLE_ORDER.length : idx;
}

function activeSortIndex(active: unknown): number {
  // 요구사항: active=0(비활성) 인원은 항상 맨 아래
  if (active === 0 || active === "0" || active === false) return 1;
  return 0;
}

export function rowActiveSortIndex<T extends Record<string, unknown>>(
  row: T
): number {
  // 데이터 소스별 키 차이(active / is_active) 모두 대응
  const fromActive = activeSortIndex(row.active);
  if (fromActive === 1) return 1;
  return activeSortIndex(row.is_active);
}

export function nurseGroupSortKey(row: {
  role?: string | null;
  active?: unknown;
  is_active?: unknown;
}): string {
  return `${rowActiveSortIndex(row)}:${nurseRoleSortIndex(row.role)}`;
}

/** 역할 순서 고정 후 sequence 오름차순 */
export function sortNurseListByRoleThenSequence<
  T extends Record<string, unknown>,
>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    const aa = rowActiveSortIndex(a);
    const ab = rowActiveSortIndex(b);
    if (aa !== ab) return aa - ab;

    const ra = nurseRoleSortIndex(a.role as string | null | undefined);
    const rb = nurseRoleSortIndex(b.role as string | null | undefined);
    if (ra !== rb) return ra - rb;
    const sa = Number(a.sequence ?? 0);
    const sb = Number(b.sequence ?? 0);
    return sa - sb;
  });
}

/** Grade 미지정: null / 0 / NaN — 정렬 시 항상 맨 뒤 (asc·desc 공통) */
function isEmptyGradeSortValue(v: unknown): boolean {
  if (v == null) return true;
  const n = Number(v);
  if (Number.isNaN(n)) return true;
  return n === 0;
}

function compareByKeyForToggle<T extends Record<string, unknown>>(
  a: T,
  b: T,
  key: keyof T,
  nextOrder: "asc" | "desc"
): number {
  if (String(key) === "grade") {
    const emptyA = isEmptyGradeSortValue(a[key]);
    const emptyB = isEmptyGradeSortValue(b[key]);
    if (emptyA && emptyB) return 0;
    if (emptyA) return 1;
    if (emptyB) return -1;
    const valA = Number(a[key]);
    const valB = Number(b[key]);
    return nextOrder === "asc" ? valA - valB : valB - valA;
  }

  const valA = a[key] ?? "";
  const valB = b[key] ?? "";

  if (typeof valA === "number" && typeof valB === "number") {
    return nextOrder === "asc" ? valA - valB : valB - valA;
  }

  const strA = String(valA);
  const strB = String(valB);
  return nextOrder === "asc"
    ? strA.localeCompare(strB, "ko")
    : strB.localeCompare(strA, "ko");
}

export const toggleSort = <T extends Record<string, unknown>>(
  data: T[],
  key: keyof T,
  currentOrder: "asc" | "desc"
): { sortedData: T[]; nextOrder: "asc" | "desc" } => {
  if (!data || data.length === 0) {
    return {
      sortedData: data,
      nextOrder: currentOrder === "asc" ? "desc" : "asc",
    };
  }

  const nextOrder = currentOrder === "desc" ? "asc" : "desc";

  const sorted = [...data].sort((a, b) =>
    compareByKeyForToggle(a, b, key, nextOrder)
  );

  const reIndexed = sorted.map((item, index) => ({
    ...item,
    sequence: index,
  }));

  return { sortedData: reIndexed, nextOrder };
};

/** 헤더 정렬: RN/AN/ETC 그룹 순서는 유지하고, 그 안에서만 name·grade·experience 정렬 */
export const toggleSortGroupedByNurseRole = <T extends Record<string, unknown>>(
  data: T[],
  key: keyof T,
  currentOrder: "asc" | "desc"
): { sortedData: T[]; nextOrder: "asc" | "desc" } => {
  if (!data || data.length === 0) {
    return {
      sortedData: data,
      nextOrder: currentOrder === "asc" ? "desc" : "asc",
    };
  }

  const nextOrder = currentOrder === "desc" ? "asc" : "desc";

  const sorted = [...data].sort((a, b) => {
    const aa = rowActiveSortIndex(a);
    const ab = rowActiveSortIndex(b);
    if (aa !== ab) return aa - ab;

    const ra = nurseRoleSortIndex(a.role as string | null | undefined);
    const rb = nurseRoleSortIndex(b.role as string | null | undefined);
    if (ra !== rb) return ra - rb;

    const cmp = compareByKeyForToggle(a, b, key, nextOrder);
    if (cmp !== 0) return cmp;

    const sa = Number(a.sequence ?? 0);
    const sb = Number(b.sequence ?? 0);
    return sa - sb;
  });

  const reIndexed = sorted.map((item, index) => ({
    ...item,
    sequence: index,
  }));

  return { sortedData: reIndexed, nextOrder };
};

export const getGroupIdQuery = (url: string, groupId: string | null) => {
  if (!groupId) return url;
  return `${url}?group_id=${encodeURIComponent(groupId)}`;
};
